import { Component, Input, OnDestroy, OnInit } from "@angular/core";
import { SepApplicationReviewDashboardService } from "./services/sep-application-review-dashboard.service";
import { AppSearchFilters, EmittedStatusObj, RowDataStructure, SearchSubmittedSepAppResponse, SepAppInfoStructure } from "./sep-application-review-grid/interfaces/sep-application-review-grid.interface";
import { SORT_OPTS, STATUS_OPTION_SERVICE_VALUES, GRID_DATA_CARD_CONFIGS } from "./sep-application-review-grid/enums/sep-application-review-grid.enum";
import { Subject, Subscription, takeUntil } from "rxjs";
import { BooleanInput } from "@ptl/global-components";
import { DataCardItem } from "../../shared/components/data-display-card/interfaces/data-display-card.interface";
import { ReducerUtilityService } from "src/app/shared/utility-services/reducer-utility.service";

@Component({
  selector: "app-sep-application-review-dashboard",
  templateUrl: "./sep-application-review-dashboard.component.html",
  styleUrl: "./sep-application-review-dashboard.component.scss",
  standalone: false
})
export class SepApplicationReviewDashboardComponent implements OnInit, OnDestroy {

  @Input() @BooleanInput() disableBulkSubmitButton: boolean = false;
  @Input() dataCardsToDisplay: string;
  @Input() headerText: string;

  dynamicDisableBulkSubmit: boolean = true;

  //enGen Color Palette
  togetherBlue = "#003963";
  splashBlue25 = "#bfe2f4"
  powerYellow = "#FFC222";
  ahnGreen = "#0CB161";
  ahnWomenMagenta = "#ca3d67"
  
  searchResponse: SearchSubmittedSepAppResponse;
  dataCardNameArray: string[];
  dataCardConfigs: {[key in STATUS_OPTION_SERVICE_VALUES]: DataCardItem} = GRID_DATA_CARD_CONFIGS;

  allApps: SepAppInfoStructure[] =[];
  allAppRowData: RowDataStructure[] = [];

  statusCounts: Record<STATUS_OPTION_SERVICE_VALUES, number> =  {
    [STATUS_OPTION_SERVICE_VALUES.NOT_STARTED]: 0,
    [STATUS_OPTION_SERVICE_VALUES.PENDING]: 0,
    [STATUS_OPTION_SERVICE_VALUES.DENIED]: 0,
    [STATUS_OPTION_SERVICE_VALUES.SUBMITTED]: 0,
    [STATUS_OPTION_SERVICE_VALUES.APPROVED]: 0
  };

  gridReadySubscription: Subscription; // observes the service for when gridApi is available
  destroy$: Subject<void> = new Subject(); // For use in emitting onDestroy

  filterAscending: AppSearchFilters = { sort: [{ key: SORT_OPTS.CREATED_DATE, value: 'asc' }] };
  
  showResetFiltersButton: boolean = false;

  bulkSubmitInProgress: boolean = false;

  constructor(
    private sepDashboardService: SepApplicationReviewDashboardService,
    private reducerUtilityService: ReducerUtilityService
  ){}

  async ngOnInit(){

    /** would like to find a way to update the data card counts in real time at some point
     * but need to be able to communicate from status dropdown on the grid to the data cards
     */

    this.dataCardNameArray = this.dataCardsToDisplay?.split(",");
    this.searchResponse = await this.sepDashboardService.fetchApplicationData(this.filterAscending);

    if(this.searchResponse.data){
      this.getDisplayCardAppCounts();
      this.allAppRowData = this.sepDashboardService.buildGridData(this.searchResponse.data);
    }

    const basicUserInfo = this.reducerUtilityService.getApplicationStates()?.LoginInfo?.basicUserInfo;
    const userFirstName = basicUserInfo?.firstName;
    this.headerText = this.headerText ? this.headerText : `Hi ${this.toTitleCase(userFirstName)}, Welcome to the SEP Enrollment Review Dashboard!`

    this.gridReadySubscription
      = this.sepDashboardService.gridReady$.pipe(takeUntil(this.destroy$))
        .subscribe(() => {
          this.showResetFiltersButton = true;
        })
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  resetFilters() {
    this.sepDashboardService.gridApi.setFilterModel(null);
    this.sepDashboardService.gridApi.setRowGroupColumns([]);
    this.sepDashboardService.gridApi.onFilterChanged();
  }

  getDisplayCardAppCounts(){
    for(const appData of this.searchResponse.data){
      this.statusCounts[appData.currentStatus]++; //for every application with that status, increment the count by one
    }
  }

  toTitleCase(str) {
    return str?.replace(/\w\S*/g, text => text.charAt(0).toUpperCase() + text.substring(1).toLowerCase());
  }

  onStatusChange(statusObj: EmittedStatusObj){ 
    /** on status change, add to the new status counts and subtract from the old status counts 
     * this is connected via input/output with the grid component and is therefore updated on change
    */
    this.statusCounts[statusObj.oldValue]--;
    this.statusCounts[statusObj.newValue]++;
  }

  onSelectionChange(selectedRows: any){
    if(selectedRows.length > 1 && !this.disableBulkSubmitButton){
      this.dynamicDisableBulkSubmit = false;
    } else if(selectedRows.length <= 1 || this.disableBulkSubmitButton){
      this.dynamicDisableBulkSubmit = true;
    }
  }

  async bulkSubmitApps(){
    this.bulkSubmitInProgress = true;
    await this.sepDashboardService.triggerBulkSubmit();
    this.bulkSubmitInProgress = false;
  }
}
